import {
  AI_ASSISTANT_RICH_CONTENT_TAGS,
  AIAssistantRichContentTagType,
  ExtractedAIContent,
} from "@/config/aiAssistantRichContent";

export interface ParsedAIContent {
  segments: ContentSegment[];
}

export interface ContentSegment {
  type: "text" | "rich";
  content: string;
  richType?: AIAssistantRichContentTagType;
  className?: string;
}

/**
 * Parse AI Assistant content and extract rich content tags
 * @param content The raw AI generated content
 * @returns Parsed content with text and rich segments
 */
export const parseAIAssistantContent = (content: string): ParsedAIContent => {
  if (!content) {
    return { segments: [] };
  }

  const segments: ContentSegment[] = [];
  // Pre-normalize single-line flattened tables by turning "| |" into line breaks
  // Example: "| H1 | H2 | | R1C1 | R1C2 | | R2C1 | R2C2 |"
  const workingContent = content
    .replace(/(\|[^\n]+\|)(\s*\|\s*\|[^\n]+\|)+/g, (m) =>
      m.replace(/\|\s*\|\s*/g, "|\n|"),
    )
    .replace(/\n\s*\|\s*\n/g, "\n");
  const allMatches: Array<{
    match: RegExpMatchArray;
    type: AIAssistantRichContentTagType;
    start: number;
    end: number;
  }> = [];

  // Find all rich content matches
  Object.entries(AI_ASSISTANT_RICH_CONTENT_TAGS).forEach(([type, config]) => {
    const tagType = type as AIAssistantRichContentTagType;
    config.regex.lastIndex = 0; // Reset regex

    let match;
    while ((match = config.regex.exec(workingContent)) !== null) {
      allMatches.push({
        match,
        type: tagType,
        start: match.index,
        end: match.index + match[0].length,
      });
    }
  });

  // Sort matches by position
  allMatches.sort((a, b) => a.start - b.start);

  // Filter out nested matches that are contained within other matches
  const filteredMatches = allMatches.filter((match, index) => {
    // Check if this match is contained within any previous match
    for (let i = 0; i < index; i++) {
      const prevMatch = allMatches[i];
      if (match.start >= prevMatch.start && match.end <= prevMatch.end) {
        // This match is nested within a previous match, exclude it
        return false;
      }
    }
    return true;
  });

  let currentIndex = 0;

  // Helper to push text while preserving intentional spacing (e.g., \n, \n\n)
  const pushTextSegment = (text: string) => {
    if (text.length === 0) return;
    // Preserve segments that either contain non-whitespace OR contain newline characters
    if (text.trim().length > 0 || /\n/.test(text) || /\\n/.test(text)) {
      segments.push({ type: "text", content: text });
    }
  };

  for (const matchInfo of filteredMatches) {
    const { match, type, start, end } = matchInfo;

    // Add text before this match (preserve whitespace/newlines)
    if (start > currentIndex) {
      let textBefore = workingContent.slice(currentIndex, start);
      // Trim boundary newlines around rich blocks to avoid blank gaps
      textBefore = textBefore.replace(/\n+$/g, "");
      pushTextSegment(textBefore);
    }

    // Add the rich content segment
    // Trim internal leading/trailing newlines from captured group
    const captured = match[1] || match[0];
    const richContent =
      typeof captured === "string"
        ? captured.replace(/^\n+|\n+$/g, "")
        : captured;
    const config = AI_ASSISTANT_RICH_CONTENT_TAGS[type];

    segments.push({
      type: "rich",
      content: richContent,
      richType: type,
      className: config.className,
    });

    currentIndex = end;
  }

  // Add remaining text (preserve whitespace/newlines)
  if (currentIndex < workingContent.length) {
    let remainingText = workingContent.slice(currentIndex);
    // Trim leading newlines after the last rich block
    remainingText = remainingText.replace(/^\n+/g, "");
    pushTextSegment(remainingText);
  }

  // If no rich content found, return the original content as text
  if (segments.length === 0) {
    segments.push({ type: "text", content: workingContent });
  }

  return { segments };
};

/**
 * Extract only rich content tags from content (for analysis)
 * @param content The AI generated content
 * @returns Array of extracted rich content items
 */
export const extractAIRichContent = (content: string): ExtractedAIContent[] => {
  const extracted: ExtractedAIContent[] = [];

  Object.entries(AI_ASSISTANT_RICH_CONTENT_TAGS).forEach(([type, config]) => {
    const tagType = type as AIAssistantRichContentTagType;
    config.regex.lastIndex = 0;

    let match;
    while ((match = config.regex.exec(content)) !== null) {
      extracted.push({
        type: tagType,
        content: match[1] || match[0],
        rawMatch: match[0],
      });
    }
  });

  return extracted;
};
